/**
 * Remembered layout shape from the last successful render of a page.
 *
 * A skeleton can only reserve the right amount of space if it knows how much
 * content is coming, and the app can't know that until the fetch resolves —
 * which is precisely the window the skeleton exists to cover. So the previous
 * visit answers for it: counts are written after a page renders for real and
 * read back the next time that page starts loading.
 *
 * Everything here is a *hint*. A wrong or missing value only makes the
 * placeholder the wrong size — never breaks the page — so every read falls
 * back to a sensible default and every write swallows its errors (private
 * mode, quota, disabled storage).
 */

const PROJECTS_KEY = 'lmm:layout:projects';
const PROJECT_KEY_PREFIX = 'lmm:layout:project:';

export interface ProjectsLayoutMemory {
  /** Total projects in the grid (favourites included). */
  total: number;
  /** How many of them were pinned as favourites (they get their own section). */
  favorites: number;
}

export interface ProjectLayoutMemory {
  /** Timeline items last seen — drives how many card placeholders to draw. */
  items: number;
  /** Whether the "Add to Timeline" card was rendered (contributor+ only). */
  canAdd: boolean;
}

// Frozen: both are shared references handed to every caller.
const DEFAULT_PROJECTS: ProjectsLayoutMemory = Object.freeze({ total: 3, favorites: 0 });
const DEFAULT_PROJECT: ProjectLayoutMemory = Object.freeze({ items: 12, canAdd: true });

/**
 * Upper bound on a recalled count.
 *
 * These numbers drive `Array.from({ length: n })`, so a corrupt or hand-edited
 * entry is not merely a wrong-sized placeholder — `{"total": 1e9}` would try to
 * render a billion cards and hang the tab. Clamping at the read is the one
 * place that covers every caller.
 */
const MAX_RECALLED_COUNT = 100;

const clampCount = (n: number) =>
  Number.isFinite(n) ? Math.min(Math.max(Math.trunc(n), 0), MAX_RECALLED_COUNT) : 0;

function read<T>(key: string, fallback: T, validate: (v: unknown) => v is T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed: unknown = JSON.parse(raw);
    return validate(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable or full — hints are optional */
  }
}

const isProjectsMemory = (v: unknown): v is ProjectsLayoutMemory =>
  !!v && typeof v === 'object'
  && typeof (v as ProjectsLayoutMemory).total === 'number'
  && typeof (v as ProjectsLayoutMemory).favorites === 'number';

const isProjectMemory = (v: unknown): v is ProjectLayoutMemory =>
  !!v && typeof v === 'object'
  && typeof (v as ProjectLayoutMemory).items === 'number'
  && typeof (v as ProjectLayoutMemory).canAdd === 'boolean';

export function recallProjectsLayout(): ProjectsLayoutMemory {
  const { total, favorites } = read(PROJECTS_KEY, DEFAULT_PROJECTS, isProjectsMemory);
  return { total: clampCount(total), favorites: clampCount(favorites) };
}

export function rememberProjectsLayout(memory: ProjectsLayoutMemory) {
  write(PROJECTS_KEY, memory);
}

export function recallProjectLayout(projectId?: string): ProjectLayoutMemory {
  if (!projectId) return DEFAULT_PROJECT;
  const { items, canAdd } = read(PROJECT_KEY_PREFIX + projectId, DEFAULT_PROJECT, isProjectMemory);
  return { items: clampCount(items), canAdd };
}

/**
 * One entry per project ever opened. They are ~40 bytes each and orphaned
 * entries (deleted projects) are harmless, so nothing prunes them — the whole
 * store stays well under a kilobyte for a realistic account.
 */
export function rememberProjectLayout(projectId: string, memory: ProjectLayoutMemory) {
  write(PROJECT_KEY_PREFIX + projectId, memory);
}

/**
 * Is there a Supabase session on disk?
 *
 * Used to decide whether an app-boot placeholder should look like the signed-in
 * page or stay neutral: showing a projects skeleton to a signed-out visitor who
 * is about to get the login screen would be a worse jump than the plain
 * spinner it replaced. supabase-js v2 stores its session under
 * `sb-<project-ref>-auth-token`.
 */
export function hasPersistedSession(): boolean {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && /^sb-.+-auth-token$/.test(key)) return true;
    }
  } catch {
    /* storage unavailable — assume signed out */
  }
  return false;
}
